import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Receipt, Clock, CheckCircle, XCircle, Plus,
  SpinnerGap, FunnelSimple, ArrowRight
} from '@phosphor-icons/react';
import { documentsAPI } from '../api/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';

const STATUS_STYLES = {
  pending_signatures: { label: 'Awaiting signatures', cls: 'bg-amber-100 text-amber-800 border-amber-400', Icon: Clock },
  signed: { label: 'Signed', cls: 'bg-emerald-100 text-emerald-800 border-emerald-400', Icon: CheckCircle },
  declined: { label: 'Declined', cls: 'bg-rose-100 text-rose-800 border-rose-400', Icon: XCircle },
  cancelled: { label: 'Cancelled', cls: 'bg-neutral-200 text-neutral-700 border-neutral-400', Icon: XCircle },
};

export default function DocumentsHub() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | needs_action | pending_signatures | signed
  const [typeFilter, setTypeFilter] = useState('all'); // all | contract | change_order

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all' && filter !== 'needs_action') params.status = filter;
      if (typeFilter !== 'all') params.document_type = typeFilter;
      const res = await documentsAPI.list(params);
      setDocs(res.data.documents || []);
    } catch (err) {
      toast({ title: 'Could not load documents', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filter changes refetch via the explicit useEffect deps below
  }, [filter, typeFilter]);

  const visible = filter === 'needs_action'
    ? docs.filter(d => d.my_signer_status === 'pending')
    : docs;

  const needsActionCount = docs.filter(d => d.my_signer_status === 'pending' && d.status === 'pending_signatures').length;

  return (
    <div className="min-h-screen bg-[#F0F0F2] pb-16" data-testid="documents-hub">
      <div className="bg-[#2B4C6F] text-white py-6 border-b-4 border-black">
        <div className="max-w-6xl mx-auto px-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-blue-100">Contracts & Change Orders</p>
            <h1 className="text-3xl md:text-4xl font-black mt-1">Documents</h1>
            <p className="text-blue-100 text-sm mt-1">
              {needsActionCount > 0 ? `${needsActionCount} document${needsActionCount === 1 ? '' : 's'} awaiting your signature` : 'All caught up'}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-3 bg-white border-2 border-black rounded-xl p-3 shadow-[3px_3px_0px_#000]">
          <FunnelSimple size={20} weight="bold" className="text-neutral-500 ml-1" />
          <div className="flex flex-wrap gap-2">
            {[
              { val: 'all', label: 'All' },
              { val: 'needs_action', label: `Needs my signature${needsActionCount ? ` (${needsActionCount})` : ''}` },
              { val: 'pending_signatures', label: 'Awaiting signatures' },
              { val: 'signed', label: 'Signed' },
              { val: 'declined', label: 'Declined' },
            ].map(f => (
              <button
                key={f.val}
                onClick={() => setFilter(f.val)}
                className={`h-9 px-3 text-sm font-bold rounded-lg border-2 border-black transition-all ${
                  filter === f.val
                    ? 'bg-[#2B4C6F] text-white shadow-[2px_2px_0px_#000]'
                    : 'bg-white text-neutral-700 hover:bg-neutral-50'
                }`}
                data-testid={`filter-${f.val}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="ml-auto flex gap-2">
            {[
              { val: 'all', label: 'All types' },
              { val: 'contract', label: 'Contracts' },
              { val: 'change_order', label: 'Change orders' },
            ].map(t => (
              <button
                key={t.val}
                onClick={() => setTypeFilter(t.val)}
                className={`h-9 px-3 text-xs font-bold rounded-lg border-2 border-black transition-all ${
                  typeFilter === t.val
                    ? 'bg-emerald-500 text-white shadow-[2px_2px_0px_#000]'
                    : 'bg-white text-neutral-700 hover:bg-neutral-50'
                }`}
                data-testid={`type-${t.val}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-16">
            <SpinnerGap size={40} weight="bold" className="text-[#2B4C6F] animate-spin mx-auto mb-3" />
            <p className="text-neutral-500">Loading documents...</p>
          </div>
        ) : visible.length === 0 ? (
          <div className="bg-white border-2 border-black rounded-xl p-12 text-center shadow-[3px_3px_0px_#000]">
            <FileText size={56} weight="light" className="text-neutral-300 mx-auto mb-3" />
            <h3 className="font-bold text-lg text-neutral-900 mb-1">No documents</h3>
            <p className="text-sm text-neutral-500">
              {filter === 'needs_action'
                ? 'Nothing awaiting your signature right now.'
                : 'Contracts and change orders you create or sign will appear here.'}
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="documents-list">
            {visible.map(doc => {
              const sty = STATUS_STYLES[doc.status] || STATUS_STYLES.pending_signatures;
              const StatusIcon = sty.Icon;
              const signedCount = (doc.signers || []).filter(s => s.status === 'signed').length;
              const totalSigners = (doc.signers || []).length;
              const isMyTurn = doc.my_signer_status === 'pending' && doc.status === 'pending_signatures';
              return (
                <li
                  key={doc._id}
                  onClick={() => navigate(`/documents/${doc._id}`)}
                  className={`group bg-white border-2 border-black rounded-xl p-5 shadow-[3px_3px_0px_#000] hover:shadow-[5px_5px_0px_#000] hover:-translate-y-0.5 cursor-pointer transition-all ${isMyTurn ? 'ring-2 ring-amber-400 ring-offset-2' : ''}`}
                  data-testid={`document-card-${doc._id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className={`w-11 h-11 rounded-lg border-2 border-black flex items-center justify-center flex-shrink-0 ${
                        doc.document_type === 'change_order' ? 'bg-purple-100' : 'bg-blue-100'
                      }`}>
                        {doc.document_type === 'change_order'
                          ? <Receipt size={22} weight="bold" className="text-purple-600" />
                          : <FileText size={22} weight="bold" className="text-[#2B4C6F]" />
                        }
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] font-black uppercase tracking-widest text-neutral-500">
                            {doc.document_type === 'change_order' ? 'Change order' : 'Contract'}
                          </span>
                          <Badge className={`${sty.cls} text-[10px] font-bold border-2`}>
                            <StatusIcon size={12} weight="bold" className="mr-1" />
                            {sty.label}
                          </Badge>
                          {isMyTurn && (
                            <Badge className="bg-amber-500 text-white text-[10px] font-bold border-2 border-black animate-pulse">
                              YOUR TURN
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-black text-neutral-900 text-lg mt-1 truncate">{doc.title}</h3>
                        <p className="text-xs text-neutral-500 mt-1">
                          {signedCount}/{totalSigners} signed · Created {new Date(doc.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <ArrowRight size={20} weight="bold" className="text-neutral-300 group-hover:text-[#2B4C6F] mt-1 flex-shrink-0" />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
